import { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Theme, CircularProgress, Box } from '@mui/material';
import { VerifyDialog } from 'components/common';
import { DIALOG_ICON_TYPES } from 'components/common/VerifyDialog/constants';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useDropzone } from 'react-dropzone';
import { GVTypography } from 'components/lib';
import ReorderIcon from '@mui/icons-material/Reorder';
import AddIcon from '@mui/icons-material/Add';
import { colors } from 'components/lib/global/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GVChip, { GVChipColor } from 'components/GVChip/GVChip';
import { getStyleVariables } from 'styles/vars';
import GVIconButton from 'components/lib/GVIconButton/GVIconButton';
import ClearIcon from '@mui/icons-material/Clear';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import cx from 'classnames';
import { FailedFile, UploadedFile, PendingFile, PendingFileStatus } from 'store/request/files/types';
import { resetRequests, stopPolling, abortRequests } from '@redux-requests/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMultipleFiles,
  mergeFiles,
  deleteFileById,
  deleteFiles,
  startAddMoreFilesCreation,
  fetchAddMoreFiles,
  pollAddMoreFilesStatus,
  finishAddMoreFilesCreation,
  pollFilesStatus,
} from 'store/request/files/actions';
import { useQuery } from '@redux-requests/react';
import { DocumentTypes, DocumentStates } from 'types';
import { FileErrors } from '@verify/shared-utils';
import { files, app, getReadyFilesQueue, getPendingIdUpdates, getFailedUploads, inspection } from 'store';
import { cancelUpload, fetchFile } from 'store/requests';
import getFileIconFromExtension from './FileHelpers';
import { makeStyles } from 'tss-react/mui';
import { getFileErrorMessage, supportedFileTypes, lowerCaseExtension } from './uploadHelpers';
import { computeSHA256 } from 'utils/computeChecksum';
import { v4 as uuidv4 } from 'uuid';

const MAX_FILES = 20;

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    dialogPaper: {
      width: '750px',
      maxWidth: 'none',
    },
    dialogRoot: {
      zIndex: 10000, // Higher than disabledOverlay (9999) to ensure it appears above everything
    },
    droppableContainer: {
      width: '700px',
      height: '280px',
      padding: theme.spacing(0.5),
      margin: theme.spacing(0.5),
      borderRadius: '2px',
      background: colors.background,
      overflowY: 'auto',
      ...styleVariables.scrollBar,
    },
    rowContainer: {
      width: '100%',
      height: '40px',
      padding: theme.spacing(1.25, 0.5),
      borderRadius: '2px',
      background: '#303236',
      marginBottom: theme.spacing(0.125),
      display: 'flex',
      justifyContent: 'space-between',
    },
    flexRules: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    dragIcon: {
      height: '15px',
      color: 'white',
    },
    deleteIcon: {
      height: '15px',
    },
    errorInfoIcon: {
      height: '15px',
    },
    fileIcon: {
      marginTop: theme.spacing(0.625),
      marginRight: theme.spacing(0.75),
    },
    iconButton: {
      padding: theme.spacing(0.5, 0.375),
      borderRadius: '50%',
      margin: theme.spacing(0.5),
      color: 'white',
      '&:hover': {
        color: '#EA3632',
      },
    },
    failedIconButton: {
      padding: theme.spacing(0.375, 0.125),
      borderRadius: '50%',
      margin: theme.spacing(0.5),
      cursor: 'default',
      '&:hover': {
        background: 'rgba(255, 74, 87, 0.32)',
      },
    },
    chip: {
      margin: '0 6px',
      '& > .MuiChip-label': {
        padding: theme.spacing(0, 1),
      },
    },
    fileName: {
      maxWidth: '510px',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    disabled: {
      opacity: '38%',
    },
    addFilesContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(1.5),
    },
    addFilesButton: {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      textTransform: 'none',
      '&:hover': {
        borderColor: theme.palette.primary.light,
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
    dropzoneActive: {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
    pendingRowContainer: {
      width: '100%',
      height: '40px',
      padding: theme.spacing(1.25, 0.5),
      borderRadius: '2px',
      background: '#303236',
      marginBottom: theme.spacing(0.125),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      opacity: 0.7,
    },
    progressContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      minWidth: '120px',
    },
    progressText: {
      fontSize: '11px',
      color: theme.palette.text.secondary,
      minWidth: '70px',
    },
  };
});

export interface ReorderFilesProps {
  uploadedFiles: UploadedFile[];
  failedFiles: FailedFile[];
  documentType: DocumentTypes;
  initialPendingFiles?: PendingFile[];
  onPendingFilesUpdate?: (pendingFiles: PendingFile[]) => void;
  removeHeadersFooters?: boolean;
}

const ReorderFiles = ({
  uploadedFiles,
  failedFiles,
  documentType,
  initialPendingFiles = [],
  onPendingFilesUpdate,
  removeHeadersFooters = true,
}: ReorderFilesProps) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const [uploadedFilesList, setUploadedFilesList] = useState<UploadedFile[]>(uploadedFiles);
  const [failedFilesList, setFailedFilesList] = useState<FailedFile[]>(failedFiles);
  const [pendingFilesList, setPendingFilesList] = useState<PendingFile[]>(initialPendingFiles);
  const [pendingAddMoreFileIds, setPendingAddMoreFileIds] = useState<string[]>([]);
  // Track which file IDs we've already processed to prevent duplicates from concurrent uploads
  const [processedFileIds, setProcessedFileIds] = useState<Set<string>>(new Set());
  // Track active polling queries (with request keys) so we can stop them - using ref to avoid effect re-runs
  const activePollingQueriesRef = useRef<string[]>([]);

  // Cleanup on unmount - stop polling and reset all add more files related requests
  useEffect(() => {
    return () => {
      // Stop all tracked polling queries
      if (activePollingQueriesRef.current.length > 0) {
        dispatch(stopPolling(activePollingQueriesRef.current));
      }
      // Also stop base polling types
      dispatch(stopPolling([pollAddMoreFilesStatus.toString(), pollFilesStatus.toString()]));
      dispatch(resetRequests([startAddMoreFilesCreation.toString()]));
      dispatch(files.actions.clearReadyFilesQueue());
      dispatch(files.actions.clearPendingIdUpdates());
      dispatch(files.actions.clearFailedUploads());
    };
  }, [dispatch]);

  // Subscribe to the ready files queue for concurrent file processing
  const readyFilesQueue = useSelector(getReadyFilesQueue);

  // Subscribe to pending ID updates queue
  const pendingIdUpdates = useSelector(getPendingIdUpdates);

  // Subscribe to failed uploads queue
  const failedUploads = useSelector(getFailedUploads);

  // Subscribe to startAddMoreFilesCreation to capture file IDs
  const { data: startAddMoreFilesData } = useQuery({ type: startAddMoreFilesCreation });

  // Process pending ID updates from the queue
  useEffect(() => {
    if (pendingIdUpdates.length > 0) {
      // Update pending files with real IDs
      setPendingFilesList((prev) => {
        const updated = [...prev];
        pendingIdUpdates.forEach(({ tempId, realId }) => {
          const index = updated.findIndex((p) => p.id === tempId);
          if (index !== -1) {
            updated[index] = {
              ...updated[index],
              id: realId,
            };
          }
        });
        return updated;
      });

      // Add real IDs to tracking
      const realIds = pendingIdUpdates.map((u) => u.realId);
      setPendingAddMoreFileIds((prev) => [...prev, ...realIds]);

      // Track polling query for this batch
      const pollingRequestKey = realIds.join('-');
      const pollingQuery = pollAddMoreFilesStatus.toString() + pollingRequestKey;
      if (!activePollingQueriesRef.current.includes(pollingQuery)) {
        activePollingQueriesRef.current = [...activePollingQueriesRef.current, pollingQuery];
      }

      // Mark as processed
      realIds.forEach((id) => {
        setProcessedFileIds((prev) => {
          const updated = new Set(prev);
          updated.add(id);
          return updated;
        });
      });

      // Clear the queue
      dispatch(files.actions.clearPendingIdUpdates());
    }
  }, [pendingIdUpdates, dispatch]);

  // Process failed uploads from the queue
  useEffect(() => {
    if (failedUploads.length > 0) {
      failedUploads.forEach((failure) => {
        // Remove pending files that failed - match by ID or filename
        setPendingFilesList((prev) =>
          prev.filter((p) => !failure.fileIds.includes(p.id) && !failure.filenames.includes(p.originalFilename)),
        );

        // Remove from pending IDs tracking
        setPendingAddMoreFileIds((prev) => prev.filter((id) => !failure.fileIds.includes(id)));
      });

      // Clear the queue
      dispatch(files.actions.clearFailedUploads());
    }
  }, [failedUploads, dispatch]);

  // Process ready files from the queue (handles concurrent fetches)
  // Note: cancelled files are already filtered out in fetchAddMoreFiles action
  useEffect(() => {
    if (readyFilesQueue.length > 0) {
      // Process all items in the queue
      readyFilesQueue.forEach((item) => {
        // Get IDs of uploaded and failed files
        const processedIds = new Set([...item.uploadedFiles.map((f) => f.id), ...item.failedFiles.map((f) => f.id)]);

        // Remove matching pending files - match by ID first, then by filename as fallback
        setPendingFilesList((prev) => {
          let remaining = [...prev];

          // Remove uploaded files
          item.uploadedFiles.forEach((uploadedFile) => {
            const index = remaining.findIndex(
              (p) =>
                p.id === uploadedFile.id ||
                (p.id.startsWith('pending-') && p.originalFilename === uploadedFile.originalFilename),
            );
            if (index !== -1) {
              remaining.splice(index, 1); // Remove this specific instance
            }
          });

          // Remove failed files
          item.failedFiles.forEach((failedFile) => {
            const index = remaining.findIndex(
              (p) =>
                p.id === failedFile.id || (p.id.startsWith('pending-') && p.originalFilename === failedFile.filename),
            );
            if (index !== -1) {
              remaining.splice(index, 1); // Remove this specific instance
            }
          });

          return remaining;
        });

        // Add new files to the lists
        if (item.uploadedFiles.length > 0) {
          setUploadedFilesList((prev) => [...prev, ...item.uploadedFiles]);
        }
        if (item.failedFiles.length > 0) {
          setFailedFilesList((prev) => [...prev, ...item.failedFiles]);
        }

        // Remove processed file IDs from pendingAddMoreFileIds
        setPendingAddMoreFileIds((prev) => prev.filter((id) => !processedIds.has(id)));

        if (item.failedFiles.length > 0) {
          dispatch(
            app.actions.setSnackMessage({
              message: `${item.failedFiles.length} file(s) failed to upload.`,
              type: 'error',
            }),
          );
        }
      });

      // Clear the queue after processing
      dispatch(files.actions.clearReadyFilesQueue());
    }
  }, [readyFilesQueue, dispatch]);

  // Notify parent when pending files change
  useEffect(() => {
    if (onPendingFilesUpdate) {
      onPendingFilesUpdate(pendingFilesList);
    }
  }, [pendingFilesList, onPendingFilesUpdate]);

  // Handle when files finish processing (uploadedFiles prop changes)
  // This matches pending files with newly processed files and transitions them
  useEffect(() => {
    if (uploadedFiles.length > 0) {
      // Remove matching pending files - match by ID first, then by filename as fallback
      setPendingFilesList((prev) => {
        let remaining = [...prev];

        uploadedFiles.forEach((uploadedFile) => {
          const index = remaining.findIndex(
            (p) =>
              p.id === uploadedFile.id ||
              (p.id.startsWith('pending-') && p.originalFilename === uploadedFile.originalFilename),
          );
          if (index !== -1) {
            remaining.splice(index, 1); // Remove this specific instance
          }
        });

        // If all pending files are now processed, clear Redux state
        if (remaining.length === 0 && prev.length > 0) {
          dispatch(files.actions.clearPendingReorderData());
        }
        return remaining;
      });

      // Add new uploaded files to our list (avoiding duplicates)
      setUploadedFilesList((prev) => {
        const existingIds = new Set(prev.map((f) => f.id));
        const newFiles = uploadedFiles.filter((f) => !existingIds.has(f.id));
        return [...prev, ...newFiles];
      });
    }
  }, [uploadedFiles, dispatch]);

  // Handle when files fail (failedFiles prop changes)
  useEffect(() => {
    if (failedFiles.length > 0) {
      // Remove matching pending files - match by ID first, then by filename as fallback
      setPendingFilesList((prev) => {
        let remaining = [...prev];

        failedFiles.forEach((failedFile) => {
          const index = remaining.findIndex(
            (p) =>
              p.id === failedFile.id || (p.id.startsWith('pending-') && p.originalFilename === failedFile.filename),
          );
          if (index !== -1) {
            remaining.splice(index, 1); // Remove this specific instance
          }
        });

        return remaining;
      });

      // Add new failed files to our list (avoiding duplicates)
      setFailedFilesList((prev) => {
        const existingIds = new Set(prev.map((f) => f.id));
        const newFiles = failedFiles.filter((f) => !existingIds.has(f.id));
        return [...prev, ...newFiles];
      });
    }
  }, [failedFiles]);

  // Function to update a pending file's status
  const updatePendingFileStatus = useCallback(
    (
      fileId: string,
      status: PendingFileStatus,
      progress?: number,
      uploadedFileData?: UploadedFile,
      error?: FileErrors,
    ) => {
      setPendingFilesList((prev) => {
        const fileIndex = prev.findIndex((f) => f.id === fileId);
        if (fileIndex === -1) return prev;

        // If file is now ready, move it to uploaded files
        if (status === PendingFileStatus.ready && uploadedFileData) {
          const newPending = prev.filter((f) => f.id !== fileId);
          setUploadedFilesList((uploaded) => [...uploaded, uploadedFileData]);
          return newPending;
        }

        // If file failed, move it to failed files
        if (status === PendingFileStatus.failed) {
          const file = prev[fileIndex];
          const newPending = prev.filter((f) => f.id !== fileId);
          setFailedFilesList((failed) => [
            ...failed,
            {
              id: file.id,
              filename: file.originalFilename,
              error: error || FileErrors.uploadError,
            },
          ]);
          return newPending;
        }

        // Otherwise, update the status
        const updated = [...prev];
        updated[fileIndex] = {
          ...updated[fileIndex],
          status,
          progress: progress !== undefined ? progress : updated[fileIndex].progress,
        };
        return updated;
      });
    },
    [],
  );

  // Function to add new pending files
  const addPendingFiles = useCallback((newFiles: PendingFile[]) => {
    setPendingFilesList((prev) => [...prev, ...newFiles]);
  }, []);

  const handleAddMoreFiles = useCallback(
    async (accepted: File[], rejected: { file: File }[]) => {
      const totalFiles =
        uploadedFilesList.length + failedFilesList.length + pendingFilesList.length + accepted.length + rejected.length;

      if (totalFiles > MAX_FILES) {
        dispatch(
          app.actions.setSnackMessage({
            message: `Cannot add more files. Maximum of ${MAX_FILES} files allowed.`,
            type: 'error',
          }),
        );
        return;
      }

      if (accepted.length === 0) {
        if (rejected.length > 0) {
          dispatch(
            app.actions.setSnackMessage({
              message: 'Unsupported file type.',
              type: 'error',
            }),
          );
        }
        return;
      }

      // Rename files with lowercase extensions
      const renamedFiles = accepted.map((file) => new File([file], lowerCaseExtension(file.name), { type: file.type }));

      // Create pending files immediately to show in the list right away
      const newPendingFiles: PendingFile[] = renamedFiles.map((file) => ({
        id: `pending-add-${uuidv4()}`,
        originalFilename: file.name,
        status: PendingFileStatus.uploading,
        progress: 0,
      }));

      // Add pending files to the list immediately
      setPendingFilesList((prev) => [...prev, ...newPendingFiles]);

      try {
        // Compute checksums and prepare FormData
        const formData = new FormData();
        await Promise.all(
          renamedFiles.map(async (file) => {
            const checksum = await computeSHA256(file);
            return { file, checksum };
          }),
        ).then((results) => {
          results.forEach(({ file, checksum }) => {
            formData.append('files', file);
            formData.append('checksums', checksum);
          });
        });

        // Prepare rejected files list
        const rejectedFilesList: FailedFile[] = rejected.map((rejectedFile) => ({
          id: `rejected-${uuidv4()}`,
          filename: lowerCaseExtension(rejectedFile.file.name),
          error: FileErrors.incompatibleFileType,
        }));

        // Create mapping of temp IDs to filenames for ID replacement
        const pendingMapping = newPendingFiles.map((p) => ({
          tempId: p.id,
          filename: p.originalFilename,
        }));

        // Dispatch the action - the chain will handle the rest
        dispatch(startAddMoreFilesCreation(formData, rejectedFilesList, pendingMapping, removeHeadersFooters));
      } catch (error) {
        // Remove pending files if upload fails
        const pendingFileIds = new Set(newPendingFiles.map((f) => f.id));
        setPendingFilesList((prev) => prev.filter((f) => !pendingFileIds.has(f.id)));

        dispatch(
          app.actions.setSnackMessage({
            message: 'There was an error uploading files. Please try again.',
            type: 'error',
          }),
        );
      }
    },
    [uploadedFilesList.length, failedFilesList.length, pendingFilesList.length, dispatch],
  );

  const {
    getRootProps,
    getInputProps,
    open: openFileBrowser,
    isDragActive,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    accept: supportedFileTypes,
    onDrop: handleAddMoreFiles,
  });

  const handleDragEnd = ({ destination, source }: DropResult) => {
    if (!destination) {
      // invalid drag end
      return;
    }
    if (destination.index === source.index) {
      // dropped to same index
      return;
    }

    const draggedItem = uploadedFilesList[source.index];
    const tempArr = [...uploadedFilesList];
    tempArr.splice(source.index, 1);
    tempArr.splice(destination.index, 0, draggedItem);
    setUploadedFilesList(tempArr);
  };

  const handleRemoveUploadedFile = (id: string) => {
    const updatedUploadedFilesList = uploadedFilesList.filter((file) => file.id !== id);

    // Check if both lists are empty, then close the dialog
    if (updatedUploadedFilesList.length === 0 && failedFilesList.length === 0) {
      handleClose();
    }
    setUploadedFilesList(updatedUploadedFilesList);
    dispatch(deleteFileById(id));
  };

  const handleRemoveFailedFile = (id: string) => {
    setFailedFilesList(failedFilesList.filter((file) => file.id !== id));
  };

  const handleClose = () => {
    // Reset fetch request first to clear multipleFilesData
    dispatch(resetRequests([fetchMultipleFiles.toString()]));

    // Set creation cancelled flag for initial batch polling
    dispatch(files.actions.setFileCreationCancelled(true));

    // Dispatch cancelUpload to abort any uploads using abortOn
    dispatch(cancelUpload());

    // Add ALL pending files to cancelled lists so they won't reappear
    // even if polling finishes after we close
    pendingFilesList.forEach((file) => {
      dispatch(files.actions.addCancelledFilename(file.originalFilename));
      if (!file.id.startsWith('pending-')) {
        dispatch(files.actions.addCancelledFileId(file.id));
      }
    });

    // Also add all tracked pending IDs
    pendingAddMoreFileIds.forEach((id) => {
      dispatch(files.actions.addCancelledFileId(id));
    });

    // Mark all active batches as cancelled
    activePollingQueriesRef.current.forEach((query) => {
      const batchKey = query.replace(pollAddMoreFilesStatus.toString(), '');
      if (batchKey) {
        dispatch(files.actions.addCancelledBatchKey(batchKey));
      }
    });

    // Stop all active polling instances (with their request keys)
    if (activePollingQueriesRef.current.length > 0) {
      dispatch(stopPolling(activePollingQueriesRef.current));
      dispatch(abortRequests(activePollingQueriesRef.current));
    }

    // Also stop any base polling types (for initial batch and as fallback)
    dispatch(stopPolling([pollAddMoreFilesStatus.toString(), pollFilesStatus.toString()]));

    // Abort other in-progress requests
    dispatch(
      abortRequests([
        startAddMoreFilesCreation.toString(),
        finishAddMoreFilesCreation.toString(),
        fetchAddMoreFiles.toString(),
        pollAddMoreFilesStatus.toString(),
        pollFilesStatus.toString(),
      ]),
    );
    dispatch(resetRequests([startAddMoreFilesCreation.toString()]));

    // Delete any files that were being uploaded when cancelled
    if (pendingAddMoreFileIds.length > 0) {
      dispatch(deleteFiles(pendingAddMoreFileIds));
      setPendingAddMoreFileIds([]);
    }

    if (uploadedFilesList.length) {
      // we soft delete the files being used for merge whenever after this window
      dispatch(deleteFiles(uploadedFilesList.map((file) => file.id)));
    }

    // Clear pending reorder data and ready files queue
    dispatch(files.actions.clearPendingReorderData());
    dispatch(files.actions.clearReadyFilesQueue());
    dispatch(files.actions.clearPendingIdUpdates());
    dispatch(files.actions.clearFailedUploads());
    dispatch(files.actions.clearFileToBatchMap());

    // Clear the multiple file status to hide the progress bar backdrop
    dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));

    // Reset the loading state to initial
    dispatch(inspection.actions.setLoadingState({ documentType, documentState: DocumentStates.INITIAL }));

    // Remove the file to clear convertingFiles status (this hides the progress bar)
    dispatch(files.actions.removeFile({ documentType }));

    // DON'T clear cancelled lists here - they need to persist in case polling finishes after close
    // They will be cleared when a new upload session starts

    // Clear local tracking state
    activePollingQueriesRef.current = [];
    setProcessedFileIds(new Set());
  };

  const handleSubmit = () => {
    if (uploadedFilesList.length === 1) {
      // fetchFile → setFetchedFile → handleAddFiles (setFile does not trigger that saga)
      dispatch(fetchFile(uploadedFilesList[0].id, documentType, true));
    } else if (uploadedFilesList.length > 1) {
      const fileIdsToMerge = uploadedFilesList.map((file) => file.id);
      dispatch(mergeFiles(fileIdsToMerge, documentType));
    }

    // Clear pending reorder data and ready files queue
    dispatch(files.actions.clearPendingReorderData());
    dispatch(files.actions.clearReadyFilesQueue());
    dispatch(files.actions.clearPendingIdUpdates());
    dispatch(files.actions.clearFailedUploads());

    dispatch(resetRequests([fetchMultipleFiles.toString()]));
  };

  const uploadedFile = (file: UploadedFile, index: number) => (
    <Draggable draggableId={file.id} key={file.id} index={index}>
      {(draggableProvided) => (
        <div
          ref={draggableProvided.innerRef}
          {...draggableProvided.draggableProps}
          {...draggableProvided.dragHandleProps}
          className={classes.rowContainer}
        >
          <GVTooltip
            title={file.originalFilename}
            arrow
            side="top"
            align="start"
            disableHoverListener={file.originalFilename.length > 70 ? false : true}
          >
            <div className={classes.flexRules}>
              <div {...draggableProvided.dragHandleProps}>
                <GVIconButton icon={<ReorderIcon className={classes.dragIcon} />} className={classes.iconButton} />
              </div>
              <GVChip chipColor={GVChipColor.SUCCESS} label={index + 1} className={classes.chip} />
              <div className={classes.fileIcon}>{getFileIconFromExtension(file.originalFilename)}</div>
              <GVTypography variant="body1" emphasis="superhigh" className={classes.fileName}>
                {file.originalFilename}
              </GVTypography>
            </div>
          </GVTooltip>
          <div className={classes.flexRules}>
            <GVIconButton
              icon={<DeleteOutlineIcon className={classes.deleteIcon} />}
              className={classes.iconButton}
              onClick={() => handleRemoveUploadedFile(file.id)}
            />
          </div>
        </div>
      )}
    </Draggable>
  );

  const failedFile = (file: FailedFile) => {
    return (
      <div className={classes.rowContainer}>
        <div className={classes.flexRules}>
          <div>
            <GVTooltip
              side="top"
              align="start"
              title={<GVTypography variant="inherit">{getFileErrorMessage(file.error as FileErrors)}</GVTypography>}
            >
              <GVIconButton
                icon={<InfoOutlinedIcon color="error" className={classes.errorInfoIcon} />}
                className={classes.failedIconButton}
              />
            </GVTooltip>
          </div>
          <div className={cx(classes.fileIcon, classes.disabled)}>{getFileIconFromExtension(file.filename)}</div>
          <GVTypography variant="body1" emphasis="disabled" className={classes.fileName}>
            {file.filename}
          </GVTypography>
        </div>
        <div className={classes.flexRules}>
          <GVTypography color="error">Failed</GVTypography>
          <GVIconButton
            icon={<ClearIcon className={classes.deleteIcon} />}
            className={classes.iconButton}
            onClick={() => handleRemoveFailedFile(file.id)}
          />
        </div>
      </div>
    );
  };

  const getStatusText = (status: PendingFileStatus) => {
    switch (status) {
      case PendingFileStatus.uploading:
        return 'Uploading...';
      case PendingFileStatus.processing:
        return 'Processing...';
      case PendingFileStatus.ready:
        return 'Ready';
      case PendingFileStatus.failed:
        return 'Failed';
      default:
        return '';
    }
  };

  const handleCancelPendingFile = (file: PendingFile) => {
    // Remove from pending files list
    setPendingFilesList((prev) => prev.filter((f) => f.id !== file.id));

    // Always add filename to cancelled list - this works even before we get real IDs
    dispatch(files.actions.addCancelledFilename(file.originalFilename));

    // If this file has a real API ID (not a temp ID), also track by ID and delete from server
    if (!file.id.startsWith('pending-')) {
      dispatch(files.actions.addCancelledFileId(file.id));
      dispatch(deleteFileById(file.id));
      setPendingAddMoreFileIds((prev) => prev.filter((id) => id !== file.id));
    }
  };

  const pendingFile = (file: PendingFile) => {
    return (
      <div key={file.id} className={classes.pendingRowContainer}>
        <div className={classes.flexRules}>
          <Box sx={{ width: '32px' }} /> {/* Spacer for alignment with draggable items */}
          <div className={classes.fileIcon}>{getFileIconFromExtension(file.originalFilename)}</div>
          <GVTypography variant="body1" emphasis="medium" className={classes.fileName}>
            {file.originalFilename}
          </GVTypography>
        </div>
        <div className={classes.flexRules}>
          <div className={classes.progressContainer}>
            {file.status === PendingFileStatus.uploading || file.status === PendingFileStatus.processing ? (
              <>
                <CircularProgress size={16} thickness={4} />
                <GVTypography className={classes.progressText}>{getStatusText(file.status)}</GVTypography>
              </>
            ) : (
              <GVTypography className={classes.progressText}>{getStatusText(file.status)}</GVTypography>
            )}
          </div>
          <GVIconButton
            icon={<ClearIcon className={classes.deleteIcon} />}
            className={classes.iconButton}
            onClick={() => handleCancelPendingFile(file)}
          />
        </div>
      </div>
    );
  };

  // Check if there are any pending files still processing
  const hasPendingFiles = pendingFilesList.some(
    (f) => f.status === PendingFileStatus.uploading || f.status === PendingFileStatus.processing,
  );

  return (
    <VerifyDialog
      title="Reorder Files"
      subTitle="Verify Compare will create a merged document with the files you've uploaded. Please select the preferred order of your document."
      open
      iconType={DIALOG_ICON_TYPES.FILE_REORDER}
      callToActionText="Continue"
      callToActionDisabled={uploadedFilesList.length < 1 || hasPendingFiles}
      handleCallToActionClick={handleSubmit}
      handleCloseModal={handleClose}
      additionalActions={
        <Button color="inherit" onClick={handleClose}>
          Cancel
        </Button>
      }
      classes={{ paper: classes.dialogPaper, root: classes.dialogRoot }}
      isDivider
    >
      <div style={{ overflowY: 'unset' }} {...getRootProps()}>
        <input id="upload-more-files" {...getInputProps()} />
        <DragDropContext onDragEnd={(result) => handleDragEnd(result)}>
          {/* Droppable represents area for where the draggable items can be dropped */}
          <Droppable droppableId="column">
            {(droppableProvided) => (
              <div
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
                className={cx(classes.droppableContainer, { [classes.dropzoneActive]: isDragActive })}
              >
                {/* Draggables represents the draggable file row items */}
                {uploadedFilesList.map((file, i) => uploadedFile(file, i))}
                {
                  droppableProvided.placeholder // placeholder used to maintain column height during drag event
                }
                {/* Pending files being uploaded/processed */}
                {pendingFilesList.map((file) => pendingFile(file))}
                {failedFilesList.map((file) => failedFile(file))}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div className={classes.addFilesContainer}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openFileBrowser}
            className={classes.addFilesButton}
          >
            Add More Files
          </Button>
        </div>
      </div>
    </VerifyDialog>
  );
};
export default ReorderFiles;
